// Enemy death animations (ENEMY-DEATHS, owner 2026-11-02: 'give the enemies death animations'; 'bigger explosions cause
// the enemies affected to disintegrate'; 'blown apart due to explosions, body parts flying everywhere the bigger the
// explosions are / finger beam should put a hole through enemies / scythe disks can cut enemies in half').
// Opt-in preview behind ?deaths=new until the owner approves (?deathlab shows every case).
//
// A killed foe keeps being drawn for a short time in its own family batch (EnemyArtRenderer.pushDead), animated purely by
// instance matrices plus per-instance shader parameters (enemyArt/dissolve.ts): no clips, no skinning, no new geometry per
// kill, no per-frame allocation. The kill event's source picks the death:
//   explosion            → BLOWN APART: the body's own part groups (head, torso halves, arms, legs, back piece; kit.ts PART)
//                          fly out from the blast centre, spin, bounce and settle; more pieces, faster and higher for
//                          bigger blasts (2.5 m → 3 pieces, 18 m → 8); faction-coloured shard chunks; no gore
//   finger               → HOLE: a round crimson-rimmed tunnel through the torso along the needle, a half-beat stagger,
//                          then the family's collapse
//   disc                 → CUT: two copies with complementary clip planes; the top half slides off along the disc's path
//                          and topples, the legs drop; teal cut faces cool to dark
//   singularity / lance  → DISINTEGRATE (only when the event carries a colour: Singularity collapse, Nova Lance rank 4+)
//   anything else        → COLLAPSE: a small deterministic variant set per family (P1), falling along the killing hit
// Every body lies still, then sinks into the ground. Budget per quality (low 40 / medium 100 / high 200 bodies, and
// 3 instance slots per body overall: a collapse uses 1, a cut 2, a blown-apart body one per piece); over it, a kill falls
// back to the old burst. Render-only: reads kill events, never touches the Sim.
import * as THREE from 'three/webgpu';
import type { EnemyKind } from '../core/content';
import { MAP_FOE_DEFS, MAP_FOE_KINDS, type MapFoeKind } from '../core/mapFoeDefs';
import { KILL_SRC } from '../core/sim';
import type { EnemyArtRenderer } from './enemyArt/renderer';
import { PART } from './enemyArt/kit';
import type { Fx } from './fx';
import { FLASH } from './a11y';

/** owner approved 2026-11-02 ('ship everything'): the new death animations are ON by default; ?deaths=old turns them off */
export function deathsNew(): boolean {
  return typeof location === 'undefined' || new URLSearchParams(location.search).get('deaths') !== 'old';
}

export const enum DV { Fwd, Back, Knee, Side, Crumple, Tumble, Flop }
export const DV_NAMES = ['forward collapse', 'backward collapse', 'knee-drop', 'side fall', 'heavy crumple', 'tumble (flier)', 'flip (crawler)'];
export const DV_SHORT = ['forward', 'backward', 'knee-drop', 'side', 'crumple', 'tumble', 'flip'];
/** death modes */
export const enum DM { Collapse, Dis, Blown, Hole, Cut }

type Kind = Exclude<EnemyKind, 'boss'>;
/** deterministic collapse variant sets per family (repeats = weight) */
export const VARIANTS: Record<Kind, DV[]> = {
  drone: [DV.Back, DV.Back, DV.Fwd, DV.Knee, DV.Side],
  lancer: [DV.Fwd, DV.Fwd, DV.Side, DV.Knee], // no backward fall: the forward-carried spear would end up pointing at the sky
  caster: [DV.Knee, DV.Knee, DV.Back, DV.Side],
  bomber: [DV.Fwd, DV.Knee, DV.Side], // killed by the player: just collapses (its contact burst is unchanged)
  splitter: [DV.Crumple, DV.Crumple, DV.Side],
  brute: [DV.Crumple, DV.Crumple, DV.Back, DV.Knee],
  stomper: [DV.Crumple, DV.Crumple, DV.Side],
  warden: [DV.Crumple, DV.Back],
  skitter: [DV.Flop],
  herald: [DV.Crumple], // THE NULL HERALD
  wisp: [DV.Tumble],
  // MAP FOES (owner 2026-11-02)
  delver: [DV.Flop, DV.Crumple], roller: [DV.Crumple, DV.Side], lurker: [DV.Flop, DV.Crumple], hopper: [DV.Flop, DV.Side], mite: [DV.Flop],
  lasher: [DV.Fwd, DV.Side, DV.Back], sporepod: [DV.Crumple], jammer: [DV.Tumble], wraith: [DV.Tumble], kite: [DV.Tumble], skyray: [DV.Tumble],
};
const HEAVY: Partial<Record<Kind, boolean>> = { brute: true, stomper: true, warden: true, splitter: true, roller: true };
/** Fractureborn monsters break into violet crystal; the armoured Severed Guard into plates with teal sparks */
const MONSTER: Partial<Record<Kind, boolean>> = { skitter: true, bomber: true, splitter: true, wisp: true, delver: true, roller: true, sporepod: true, lasher: true, wraith: true, hopper: true, mite: true, kite: true, skyray: true, lurker: true };
const HOVER: Partial<Record<Kind, number>> = { wisp: 1.4, jammer: 1.5, wraith: 1.1, kite: 1.8, skyray: 2.4 };
const RADIUS: Record<Kind, number> = { drone: 0.6, skitter: 0.45, brute: 1.1, caster: 0.6, lancer: 0.6, stomper: 1.3, bomber: 0.55, splitter: 0.9, wisp: 0.35, warden: 1.2, herald: 1.5,
  ...Object.fromEntries(MAP_FOE_KINDS.map((k) => [k, MAP_FOE_DEFS[k].radius])) as Record<MapFoeKind, number> }; // MAP FOES

/** per-quality cap on simultaneous dying bodies (every mode shares it) */
export const DEATH_CAP: Record<string, number> = { low: 40, medium: 100, high: 200 };
const EMBERS: Record<string, number> = { low: 7, medium: 12, high: 18 };
const DIS_T = 0.72;    // disintegrate duration (s)
const HOLD_T = 0.35;   // lie still after the fall
const SINK_T = 0.8;    // sink into the ground
const STAGGER_T = 0.22; // finger beam: half a beat before the collapse
const GRAV = 16, PGRAV = 18;
const HOLE_COL = 0xff2a3a, CUT_COL = 0x5fffe0;

const { head: H, upper: U, lower: L, armL: AL, armR: AR, legL: LL, legR: LR, back: BK } = PART;
const b_ = (...g: number[]) => g.reduce((m, k) => m | (1 << k), 0);
/** how the 8 part groups are grouped into k flying pieces (3..8) */
const SPLITS: number[][] = [
  [], [], [],
  [b_(H, U, AL, BK), b_(AR), b_(L, LL, LR)],
  [b_(H), b_(U, AL, AR, BK), b_(L, LL), b_(LR)],
  [b_(H), b_(U, AL, BK), b_(AR), b_(L, LL), b_(LR)],
  [b_(H), b_(U, BK), b_(AL), b_(AR), b_(L, LL), b_(LR)],
  [b_(H), b_(U, BK), b_(AL), b_(AR), b_(L), b_(LL), b_(LR)],
  [b_(H), b_(U), b_(AL), b_(AR), b_(L), b_(LL), b_(LR), b_(BK)],
];

interface Piece {
  mask: number; cx: number; cy: number; cz: number; rad: number;
  px: number; py: number; pz: number; vx: number; vy: number; vz: number;
  q: THREE.Quaternion; ax: number; ay: number; az: number; w: number; rest: boolean;
}
/** one dying body (pooled, reused) */
interface Body {
  kind: Kind; elite: boolean; mode: DM; v: DV; heavy: boolean;
  x: number; z: number; dx: number; dz: number; s: number; h: number; hm: number; r: number;
  yaw0: number; yawT: number; air: number; t: number; fallT: number; life: number; thumped: boolean;
  col: number; cr: number; cg: number; cb: number; bx: number; bz: number; emitted: number;
  ang: number; ya: number; yb: number; slots: number; np: number; pieces: Piece[]; settle: number;
}
/** what a kill hands over (the Sim kill event satisfies it; the lab builds its own) */
export interface DeathIn { x: number; z: number; kind: EnemyKind; elite: boolean; src: number; r: number; col: number; dx: number; dz: number; vx: number; vz: number; s: number; lift: number; boss: boolean }

const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _qf = new THREE.Quaternion(), _p = new THREE.Vector3(), _s = new THREE.Vector3();
const _ax = new THREE.Vector3(), _w = new THREE.Vector3(), _c = new THREE.Color(), _bc = new THREE.Color(), _v = new THREE.Vector3();
const UPV = new THREE.Vector3(0, 1, 0);

const ease = (k: number) => k * k * (3 - 2 * k);
const clamp01 = (k: number) => (k < 0 ? 0 : k > 1 ? 1 : k);
function lerpAngle(a: number, b: number, k: number): number { let d = (b - a) % (Math.PI * 2); if (d > Math.PI) d -= Math.PI * 2; if (d < -Math.PI) d += Math.PI * 2; return a + d * k; }
/** deterministic small hash of the death spot (same run → same variant) */
function spotHash(x: number, z: number, salt: number): number {
  let h = Math.imul((Math.round(x * 16) | 0) ^ 0x9e3779b1, 0x85ebca6b) ^ Math.imul((Math.round(z * 16) | 0) + salt, 0xc2b2ae35);
  h ^= h >>> 15; h = Math.imul(h, 0x27d4eb2f); h ^= h >>> 13;
  return h >>> 0;
}
/** death mode for a kill (exported for the lab / QA) */
export function deathMode(src: number, col: number): DM {
  if (src === KILL_SRC.explosion) return DM.Blown;
  if (src === KILL_SRC.finger) return DM.Hole;
  if (src === KILL_SRC.disc) return DM.Cut;
  if ((src === KILL_SRC.singularity || src === KILL_SRC.lance) && col >= 0) return DM.Dis;
  return DM.Collapse;
}
/** number of flying pieces for a blast radius (2.5 m → 3, 18 m → 8) */
export const piecesFor = (r: number) => Math.max(3, Math.min(8, 3 + Math.round(((r - 2.5) / 15.5) * 5)));

export class EnemyDeaths {
  readonly cap: number;
  private pool: Body[] = [];
  private n = 0;
  private slots = 0;
  private maxSlots: number;
  private embers: number;
  /** QA: instances that could not be drawn because a batch was full / kills that fell back over the budget */
  dropped = 0; overCap = 0;
  /** optional: scorch the ground under big-blast remains */
  scorch: ((x: number, z: number, r: number) => void) | null = null;
  constructor(private art: EnemyArtRenderer, private fx: Fx, private groundAt: (x: number, z: number) => number, quality: string, cap?: number) {
    this.cap = cap ?? DEATH_CAP[quality] ?? 100;
    this.maxSlots = this.cap * 3;
    this.embers = EMBERS[quality] ?? 12;
    for (let i = 0; i < this.cap; i++) {
      const pieces: Piece[] = [];
      for (let k = 0; k < 8; k++) pieces.push({ mask: 0, cx: 0, cy: 0, cz: 0, rad: 0, px: 0, py: 0, pz: 0, vx: 0, vy: 0, vz: 0, q: new THREE.Quaternion(), ax: 0, ay: 1, az: 0, w: 0, rest: false });
      this.pool.push({ kind: 'drone', elite: false, mode: DM.Collapse, v: DV.Back, heavy: false, x: 0, z: 0, dx: 0, dz: 1, s: 1, h: 2, hm: 2, r: 0.6, yaw0: 0, yawT: 0, air: 0, t: 0, fallT: 0, life: 1, thumped: false,
        col: -1, cr: 1, cg: 1, cb: 1, bx: 0, bz: 0, emitted: 0, ang: 0, ya: 0, yb: 0, slots: 1, np: 0, pieces, settle: 0 });
    }
  }
  get active(): number { return this.n; }
  clear(): void { this.n = 0; this.slots = 0; }

  /** deterministic collapse variant for a kill (exported for the lab / QA) */
  static variant(kind: Kind, x: number, z: number): DV {
    const set = VARIANTS[kind]; return set[spotHash(x, z, kind.length) % set.length];
  }

  /**
   * Start a death. hx/hz = hero position (faces foes that were standing still). Returns true when the new death handles
   * it (the caller skips the old burst), false for a boss / unknown kind / over the budget (old behaviour).
   */
  kill(ev: DeathIn, hx: number, hz: number, force?: { mode?: DM; v?: DV }): boolean {
    if (ev.boss || ev.kind === 'boss') return false;
    const kind = ev.kind as Kind;
    const mode = force?.mode ?? deathMode(ev.src, ev.col);
    const parts = mode === DM.Blown ? this.art.parts(kind, ev.elite) : null;
    const want = mode === DM.Cut ? 2 : mode === DM.Blown ? piecesFor(ev.r) : 1;
    if (this.n >= this.cap || this.slots + want > this.maxSlots || (mode === DM.Blown && !parts)) { this.overCap++; return false; }
    const b = this.pool[this.n++];
    b.kind = kind; b.elite = ev.elite; b.mode = mode; b.heavy = !!HEAVY[kind] || ev.elite;
    b.v = force?.v ?? EnemyDeaths.variant(kind, ev.x, ev.z);
    b.x = ev.x; b.z = ev.z; b.s = ev.s || 1; b.hm = this.art.height(kind); b.h = b.hm * b.s; b.r = RADIUS[kind] * b.s;
    const dl = Math.hypot(ev.dx, ev.dz);
    if (dl > 1e-6) { b.dx = ev.dx / dl; b.dz = ev.dz / dl; } else { const a = Math.hypot(ev.x - hx, ev.z - hz) || 1; b.dx = (ev.x - hx) / a; b.dz = (ev.z - hz) / a; }
    const spd = Math.hypot(ev.vx, ev.vz);
    b.yaw0 = kind !== 'warden' && spd > 0.4 ? Math.atan2(ev.vx, ev.vz) : Math.atan2(hx - ev.x, hz - ev.z);
    b.air = Math.max(0, ev.lift) + (HOVER[kind] ?? 0);
    b.t = 0; b.thumped = false; b.emitted = 0; b.col = ev.col; b.slots = want; b.np = 0; b.settle = 0;
    this.slots += want;
    _bc.setHex(mode === DM.Hole ? HOLE_COL : mode === DM.Cut ? CUT_COL : ev.col >= 0 ? ev.col : 0xffffff); b.cr = _bc.r; b.cg = _bc.g; b.cb = _bc.b;
    // model-space angle of the hit direction (hole / cut / dissolve side)
    b.ang = Math.atan2(b.dx, b.dz) - b.yaw0;
    if (mode === DM.Dis) { b.bx = -b.dx; b.bz = -b.dz; b.ang = Math.atan2(b.bx, b.bz) - b.yaw0; b.life = DIS_T; return true; }
    if (mode === DM.Blown) { this.blowApart(b, ev, parts!); return true; }
    if (mode === DM.Cut) {
      b.ya = b.hm * (0.52 + (spotHash(ev.x, ev.z, 7) % 100) / 100 * 0.12); // cut height (model units)
      b.yb = ((spotHash(ev.x, ev.z, 3) & 1) ? 1 : -1) * 0.14;             // tilt from the disc's spin
      b.fallT = 0.75; b.life = b.fallT + 0.18 + HOLD_T + SINK_T;
      this.cutFx(b);
      this.tinyBurst(b);
      return true;
    }
    this.setupCollapse(b, ev.lift);
    if (mode === DM.Hole) {
      b.ya = b.hm * 0.6; b.yb = Math.max(0.07, Math.min(0.24, b.hm * 0.085)); // hole height / radius (model units)
      b.fallT += STAGGER_T; b.life += STAGGER_T;
      this.holeFx(b);
    }
    this.tinyBurst(b);
    return true;
  }

  private setupCollapse(b: Body, lift: number): void {
    const v = b.v, toward = Math.atan2(-b.dx, -b.dz), along = Math.atan2(b.dx, b.dz);
    switch (v) {
      case DV.Fwd: b.yawT = along; break; // spun round by the hit, falls face-first along it
      case DV.Side: { const a1 = along + Math.PI / 2, a2 = along - Math.PI / 2; b.yawT = Math.abs(lerpAngle(b.yaw0, a1, 1) - b.yaw0) < Math.abs(lerpAngle(b.yaw0, a2, 1) - b.yaw0) ? a1 : a2; break; }
      case DV.Back: case DV.Knee: b.yawT = toward; break;
      default: b.yawT = b.yaw0;
    }
    if (b.mode === DM.Hole) b.yawT = b.yaw0; // keep the hole lined up with the needle that made it
    const drop = Math.sqrt((2 * Math.max(0, lift)) / GRAV);
    b.fallT = v === DV.Fwd || v === DV.Back || v === DV.Side ? 0.5 * (b.heavy ? 1.2 : 1)
      : v === DV.Knee ? 0.78 : v === DV.Crumple ? 0.85 : v === DV.Flop ? 0.42 : Math.max(0.35, Math.sqrt((2 * b.air) / GRAV));
    if (v !== DV.Tumble) b.fallT = Math.max(b.fallT, drop + 0.15);
    b.life = b.fallT + 0.18 + HOLD_T + SINK_T;
  }

  /** advance and draw every dying body into the family batches; call between art.begin() (after live foes) and art.end() */
  draw(dt: number): void {
    for (let i = 0; i < this.n; i++) {
      const b = this.pool[i];
      b.t += dt;
      if (b.t >= b.life) { this.slots -= b.slots; const last = this.pool[--this.n]; this.pool[this.n] = b; this.pool[i] = last; i--; continue; }
      const gy = this.groundAt(b.x, b.z);
      if (b.mode === DM.Dis) { if (!this.placeDis(b, b.t, gy)) this.dropped++; }
      else if (b.mode === DM.Blown) this.placePieces(b, dt);
      else if (b.mode === DM.Cut) this.placeCut(b, b.t, gy);
      else if (!this.placeCollapse(b, gy)) this.dropped++;
    }
  }

  // ------------------------------------------------------------------ collapse (+ finger hole)
  private placeCollapse(b: Body, gy: number): boolean {
    const hole = b.mode === DM.Hole;
    const t = hole ? Math.max(0, b.t - STAGGER_T) : b.t;
    const ft = hole ? b.fallT - STAGGER_T : b.fallT;
    const k = clamp01(t / ft);
    const yaw = lerpAngle(b.yaw0, b.yawT, ease(clamp01(t / Math.min(0.3, ft * 0.6))));
    let fdx = b.dx, fdz = b.dz;
    let theta = 0, sy = 1, sxz = 1, sink = 0, pivH = 0, pivR = b.r * 0.55, drift = 0, hop = 0;
    // finger: the needle punches through, the body jolts back half a beat before it goes down
    if (hole && b.t < STAGGER_T) { const u = b.t / STAGGER_T; theta = 0.16 * Math.sin(u * Math.PI * 0.5) - 0.05 * Math.sin(u * Math.PI * 3); drift = 0.12 * b.s * u; }
    else switch (b.v) {
      case DV.Fwd: case DV.Back: case DV.Side:
        theta = (b.v === DV.Side ? 1.52 : 1.5) * k * k;
        drift = (b.v === DV.Back ? 0.8 : 0.55) * b.s * Math.sin(k * Math.PI / 2);
        break;
      case DV.Knee: {
        const ka = ease(clamp01(t / 0.3)), kb = clamp01((t - 0.3) / (ft - 0.3));
        sink = 0.3 * b.h * ka; pivH = sink; drift = 0.35 * b.s * ka;
        fdx = -b.dx; fdz = -b.dz;
        theta = -0.12 * Math.sin(ka * Math.PI) + 1.38 * kb * kb;
        break;
      }
      case DV.Crumple: {
        const ka = ease(clamp01(t / 0.45)), kb = clamp01((t - 0.45) / (ft - 0.45));
        sy = 1 - 0.32 * ka; sxz = 1 + 0.1 * ka; sink = 0.08 * b.h * ka;
        theta = 0.3 * ka + 0.95 * kb * kb; drift = 0.4 * b.s * ka;
        break;
      }
      case DV.Flop:
        hop = 0.45 * b.s * Math.sin(Math.PI * k); theta = Math.PI * ease(k);
        pivH = b.h * 0.5; pivR = 0; fdx = Math.cos(yaw); fdz = -Math.sin(yaw);
        drift = 1.0 * b.s * Math.sin(k * Math.PI / 2);
        break;
      case DV.Tumble:
        theta = (Math.PI * 2.5) * k; pivH = b.h * 0.5; pivR = 0; drift = 1.2 * b.s * k;
        break;
    }
    const after = t - ft;
    if (after > 0) {
      if (b.v !== DV.Tumble && b.v !== DV.Flop) theta -= 0.1 * Math.sin(Math.PI * clamp01(after / 0.18));
      else hop += 0.18 * b.s * Math.sin(Math.PI * clamp01(after / 0.2));
      if (!b.thumped) { b.thumped = true; this.thump(b, fdx, fdz, drift); }
    }
    const sinkK = clamp01((after - 0.18 - HOLD_T) / SINK_T);
    sink += ease(sinkK) * (0.55 * b.h + 0.2);
    let air = 0;
    if (b.v === DV.Tumble && !(hole && b.t < STAGGER_T)) air = b.air * (1 - k * k);
    else if (b.v === DV.Tumble) air = b.air;
    else if (b.air > 0) air = Math.max(0, b.air - 0.5 * GRAV * t * t);
    this.compose(b, yaw, fdx, fdz, theta, pivR, pivH, drift, gy + air + hop - sink, sxz, sy);
    const glowK = (b.elite ? 1.5 : 1) * Math.max(0, 1 - b.t / 0.22);
    const dim = 1 - 0.35 * clamp01(b.t / 0.4) - 0.25 * sinkK;
    const flash = b.t < 0.06 ? 1 + 0.6 * FLASH.L.hit : 1; // a11y: the death pop's white flash follows Reduce flashing
    _c.setRGB(dim * flash, dim * flash, dim * flash);
    if (!hole) return this.art.pushDead(b.kind, b.elite, _m, _c, glowK);
    const heat = Math.max(0, 1 - b.t / 0.75);
    return this.art.pushDead(b.kind, b.elite, _m, _c, glowK, 0, b.cr, b.cg, b.cb, b.ang, 2, b.ya, b.yb, heat);
  }

  /** fall rotation about the horizontal axis perpendicular to (fdx, fdz), pivot at the feet edge (or a height) → _m */
  private compose(b: Body, yaw: number, fdx: number, fdz: number, theta: number, pivR: number, pivH: number, drift: number, y: number, sxz: number, sy: number): void {
    _ax.set(fdz, 0, -fdx); _qf.setFromAxisAngle(_ax, theta);
    _q.setFromAxisAngle(UPV, yaw).premultiply(_qf);
    _w.set(fdx * pivR, pivH, fdz * pivR);
    _p.copy(_w).applyQuaternion(_qf).negate().add(_w); // W - Qf·W
    _p.x += b.x + b.dx * drift; _p.y += y; _p.z += b.z + b.dz * drift;
    _s.set(b.s * sxz, b.s * sy, b.s * sxz);
    _m.compose(_p, _q, _s);
  }

  // ------------------------------------------------------------------ disc cut
  private placeCut(b: Body, t: number, gy: number): void {
    const heat = Math.max(0, 1 - t / 0.8), cutW = b.ya * b.s;
    const sinkK = clamp01((t - b.fallT - 0.18 - HOLD_T) / SINK_T), sink = ease(sinkK) * (0.55 * b.h + 0.2);
    const dim = 1 - 0.3 * clamp01(t / 0.4) - 0.25 * sinkK, glowK = (b.elite ? 1.5 : 1) * Math.max(0, 1 - t / 0.22);
    _c.setRGB(dim, dim, dim);
    // bottom half: stands a moment, then the legs give way backward
    const kb = clamp01((t - 0.22) / 0.5);
    this.compose(b, b.yaw0, -b.dx, -b.dz, 1.25 * kb * kb, b.r * 0.5, 0, 0.1 * b.s * kb, gy + b.air * (1 - kb) - sink, 1, 1);
    if (!this.art.pushDead(b.kind, b.elite, _m, _c, glowK, 0, b.cr, b.cg, b.cb, b.ang, 3, b.ya, b.yb, heat)) this.dropped++;
    // top half: slides off along the disc's path, tips over its leading edge and drops to the ground
    const kt = clamp01((t - 0.06) / 0.55);
    const slide = 0.35 * b.s * clamp01(t / 0.12) + 0.9 * b.s * ease(kt);
    const theta = 1.45 * kt * kt, fall = cutW * kt * kt;
    this.compose(b, b.yaw0, b.dx, b.dz, theta, b.r * 0.6, cutW, slide, gy + b.air * (1 - kt) - fall - sink, 1, 1);
    if (!this.art.pushDead(b.kind, b.elite, _m, _c, glowK, 0, b.cr, b.cg, b.cb, b.ang, 4, b.ya, b.yb, heat)) this.dropped++;
    if (!b.thumped && kt >= 1) { b.thumped = true; this.thump(b, b.dx, b.dz, slide); }
  }
  /** teal slash streak along the disc's path through the body, at the cut height */
  private cutFx(b: Body): void {
    const y = this.groundAt(b.x, b.z) + b.air + b.ya * b.s, px = -b.dz, pz = b.dx;
    for (let k = 0; k < 12; k++) {
      const u = k / 11 - 0.5, o = u * b.r * 3.2;
      this.fx.particles.emitDir(b.x + b.dx * o + px * (Math.random() - 0.5) * 0.2, y + (Math.random() - 0.5) * 0.08, b.z + b.dz * o + pz * (Math.random() - 0.5) * 0.2, b.dx * 9, 0.2, b.dz * 9, CUT_COL, 0.2, 0.18 + Math.random() * 0.1, 9);
    }
    this.fx.stars.add(b.x, y, b.z, 1.1 * b.s, 0.12, CUT_COL, 2);
  }
  /** crimson sparks and embers out of the back of the hole */
  private holeFx(b: Body): void {
    const y = this.groundAt(b.x, b.z) + b.air + b.ya * b.s;
    for (let k = 0; k < 9; k++) {
      const sp = 5 + Math.random() * 7;
      this.fx.particles.emitDir(b.x + b.dx * b.r * 0.6, y, b.z + b.dz * b.r * 0.6, b.dx * sp + (Math.random() - 0.5) * 2, (Math.random() - 0.3) * 2.5, b.dz * sp + (Math.random() - 0.5) * 2,
        Math.random() < 0.4 ? 0xffb060 : HOLE_COL, 0.12 + Math.random() * 0.1, 0.25 + Math.random() * 0.3, 9);
    }
  }

  // ------------------------------------------------------------------ blown apart
  private blowApart(b: Body, ev: DeathIn, parts: Float32Array): void {
    const r = Math.max(1, ev.r), v = clamp01((r - 2.5) / 15.5), split = SPLITS[piecesFor(r)];
    const cy = Math.cos(b.yaw0), sy = Math.sin(b.yaw0);
    const gy = this.groundAt(b.x, b.z);
    let np = 0;
    for (const mask of split) {
      let n = 0, cx = 0, cyy = 0, cz = 0;
      for (let g = 0; g < 8; g++) if ((mask >> g) & 1 && parts[g * 4 + 3] > 0) { cx += parts[g * 4]; cyy += parts[g * 4 + 1]; cz += parts[g * 4 + 2]; n++; }
      if (!n) continue;
      cx /= n; cyy /= n; cz /= n;
      let rad = 0;
      for (let g = 0; g < 8; g++) if ((mask >> g) & 1 && parts[g * 4 + 3] > 0) rad = Math.max(rad, Math.hypot(parts[g * 4] - cx, parts[g * 4 + 1] - cyy, parts[g * 4 + 2] - cz) + parts[g * 4 + 3]);
      const p = b.pieces[np++];
      p.mask = mask; p.cx = cx; p.cy = cyy; p.cz = cz; p.rad = Math.max(0.12, rad * 0.55);
      // world start: the part's place on the standing body
      const wx = cx * cy + cz * sy, wz = -cx * sy + cz * cy;
      p.px = b.x + wx * b.s; p.py = gy + b.air + cyy * b.s; p.pz = b.z + wz * b.s;
      // launch: outward from the blast (plus the part's own offset from the body centre), up, spinning
      const spread = (Math.random() - 0.5) * 1.1, ca = Math.cos(spread), sa = Math.sin(spread);
      let ox = b.dx * ca - b.dz * sa, oz = b.dx * sa + b.dz * ca;
      const ol = Math.hypot(wx, wz); if (ol > 1e-3) { ox += (wx / ol) * 0.5; oz += (wz / ol) * 0.5; }
      const on = Math.hypot(ox, oz) || 1;
      const hs = (2.5 + 6.5 * v) * (0.7 + Math.random() * 0.6) / Math.sqrt(b.s);
      p.vx = (ox / on) * hs; p.vz = (oz / on) * hs;
      p.vy = (3 + 9 * v) * (0.6 + Math.random() * 0.7) + (cyy / Math.max(0.5, b.hm)) * 2;
      p.q.setFromAxisAngle(UPV, b.yaw0);
      _v.set(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5).normalize();
      p.ax = _v.x; p.ay = _v.y; p.az = _v.z; p.w = (5 + 12 * v) * (0.6 + Math.random() * 0.8);
      p.rest = false;
    }
    b.np = np;
    this.slots -= b.slots - np; b.slots = np;
    b.life = 4; // trimmed once every piece has settled
    // small chunks: violet crystal for the Fractureborn, armour shards + teal sparks for the Severed Guard, gold for elites
    const y = gy + b.air + b.h * 0.5, mon = MONSTER[b.kind];
    const nShard = Math.round(4 + 10 * v), sp = 5 + 9 * v;
    this.fx.shatter(b.x, y, b.z, nShard, sp, b.elite ? 0xffd060 : mon ? 0xb080ff : 0xd8d0c0, 0.32 + 0.2 * v);
    if (!mon) this.fx.particles.emit(b.x, y, b.z, Math.round(5 + 8 * v), sp * 0.8, 0x3fe0d0, 0.16, 0.45, 2, 4, 2, 3);
    if (b.elite) this.fx.particles.emit(b.x, y, b.z, 6, sp, 0xffd060, 0.18, 0.5, 2, 4, 2, 3);
    if (r >= 6 && this.scorch) this.scorch(b.x, b.z, 0.8 * b.s + 0.1 * r);
  }
  private placePieces(b: Body, dt: number): void {
    const t = b.t;
    let allRest = true;
    for (let k = 0; k < b.np; k++) {
      const p = b.pieces[k];
      const gy = this.groundAt(p.px, p.pz), floor = gy + p.rad * b.s * 0.45;
      if (!p.rest) {
        allRest = false;
        p.vy -= PGRAV * dt; p.px += p.vx * dt; p.py += p.vy * dt; p.pz += p.vz * dt;
        _v.set(p.ax, p.ay, p.az); _qf.setFromAxisAngle(_v, p.w * dt); p.q.premultiply(_qf);
        if (p.py < floor) {
          p.py = floor;
          if (p.vy < 0) {
            if (-p.vy < 2.2 || t > 2.6) { p.rest = true; p.vx = p.vy = p.vz = 0; p.w = 0; if (k === 0 && !b.thumped) { b.thumped = true; } }
            else {
              p.vy = -p.vy * 0.32; p.vx *= 0.55; p.vz *= 0.55; p.w *= 0.5;
              if (Math.random() < 0.5) this.fx.particles.emit(p.px, gy + 0.12, p.pz, 2, 1.6, 0x8a7a66, 0.35 * Math.min(1.5, b.s), 0.3, 0.2, 0, 3, 2);
            }
          }
        }
      }
    }
    if (allRest && !b.settle) { b.settle = t; b.life = t + HOLD_T + SINK_T; }
    const sinkK = b.settle ? clamp01((t - b.settle - HOLD_T) / SINK_T) : 0;
    const dim = 1 - 0.35 * clamp01(t / 0.5) - 0.25 * sinkK, glowK = (b.elite ? 1.5 : 1) * Math.max(0, 1 - t / 0.3);
    _c.setRGB(dim, dim, dim);
    for (let k = 0; k < b.np; k++) {
      const p = b.pieces[k];
      // matrix: piece centre at p, spun by q about its own centroid
      _s.set(b.s, b.s, b.s);
      _w.set(p.cx * b.s, p.cy * b.s, p.cz * b.s).applyQuaternion(p.q);
      _p.set(p.px - _w.x, p.py - _w.y - ease(sinkK) * (p.rad * b.s + 0.25), p.pz - _w.z);
      _m.compose(_p, p.q, _s);
      if (!this.art.pushDead(b.kind, b.elite, _m, _c, glowK, 0, 1, 1, 1, 0, 1, p.mask, 0, 0)) this.dropped++;
    }
  }

  // ------------------------------------------------------------------ disintegrate
  private placeDis(b: Body, t: number, gy: number): boolean {
    const u = Math.max(1e-3, t / DIS_T);
    _q.setFromAxisAngle(UPV, b.yaw0);
    _p.set(b.x, gy + b.air + 0.12 * b.s * u * u, b.z);
    _s.set(b.s, b.s, b.s);
    _m.compose(_p, _q, _s);
    // base goes dark as the emissive silhouette takes over (starts from the normal look, so a paused frame never reads black)
    const base = 1 - 0.985 * clamp01(t / 0.05);
    _c.setRGB(base, base, base);
    const gk = 0.35 * (1 - u) * (1 - u);
    const ok = this.art.pushDead(b.kind, b.elite, _m, _c, gk, u, b.cr * 1.6, b.cg * 1.6, b.cb * 1.6, b.ang);
    const want = Math.floor(this.embers * (b.elite ? 1.5 : 1) * clamp01((u - 0.12) / 0.75) * Math.min(1.6, Math.max(0.8, b.s * b.h / 2)));
    const col = b.col >= 0 ? b.col : 0xffffff;
    while (b.emitted < want) {
      b.emitted++;
      const side = (Math.random() - 0.5) * 2 * b.r, hy = Math.random() * b.h * 0.95;
      const px = b.x + b.bx * b.r * 0.4 * (Math.random() - 0.3) + b.bz * side, pz = b.z + b.bz * b.r * 0.4 * (Math.random() - 0.3) - b.bx * side;
      const sp = 1.6 + Math.random() * 2.2;
      this.fx.particles.emitDir(px, gy + b.air + hy, pz, -b.bx * sp + (Math.random() - 0.5), 1.4 + Math.random() * 2.4, -b.bz * sp + (Math.random() - 0.5),
        Math.random() < 0.3 ? 0xffffff : col, (0.12 + Math.random() * 0.14) * Math.min(1.6, b.s), 0.5 + Math.random() * 0.35, 9);
    }
    return ok;
  }

  // ------------------------------------------------------------------ small effects
  /** the kill puff for physical deaths: a few faction-coloured shards (the old explosion is gone with the new deaths) */
  private tinyBurst(b: Body): void {
    const mon = MONSTER[b.kind], y = this.groundAt(b.x, b.z) + b.air + b.h * 0.55;
    this.fx.shatter(b.x, y, b.z, b.elite || b.heavy ? 4 : 2, 3, b.elite ? 0xfff0b0 : mon ? 0xd0b8ff : 0xb8f4f0, 0.26);
  }
  /** impact: dust where the body lands; heavy foes thump (bigger dust ring, a touch of shake) */
  private thump(b: Body, fdx: number, fdz: number, drift: number): void {
    const reach = b.v === DV.Flop || b.v === DV.Tumble ? 0 : b.h * 0.5;
    const x = b.x + b.dx * drift + fdx * reach, z = b.z + b.dz * drift + fdz * reach, y = this.groundAt(x, z) + 0.15;
    if (b.heavy) {
      this.fx.particles.emit(x, y, z, 9, 3.6 * Math.min(1.5, b.s), 0x8a7a66, 0.75 * Math.min(1.5, b.s), 0.55, 0.6, 0, 3, 2);
      this.fx.rings.add(x, y - 0.08, z, b.h * 0.7, 0.35, 0x9a8a72, 0.6, 0.6);
      this.fx.shake = Math.min(1, this.fx.shake + 0.04);
    } else this.fx.particles.emit(x, y, z, 4, 2.4, 0x8a7a66, 0.5, 0.4, 0.3, 0, 3, 2);
  }
}
